import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { autoAssignApi } from '../../api/autoAssign';
import { teamsApi } from '../../api/teams';
import { companiesApi } from '../../api/companies';
import { categoriesApi } from '../../api/categories';
import { productLinesApi } from '../../api/productLines';
import { useCompany } from '../../contexts/CompanyContext';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Zap, GripVertical } from 'lucide-react';
import type { AutoAssignRule } from '../../types';
import EmptyState from '../EmptyState';
import { useClassifications } from '../../hooks/useClassifications';

// Priorities and sources are per-company (Company Settings -> Classification).
// A rule can name a value that has since been retired, so the pickers below
// show every def, not only the active ones — otherwise an existing rule's
// condition would vanish from the editor while still matching tickets.

interface FormData {
  name: string;
  priority: number;
  isActive: boolean;
  conditions: {
    categories?: string[];
    priorities?: string[];
    sources?: string[];
    productLines?: string[];
  };
  assignToTechId: string;
  assignToTeamId: string;
  setProductLineId: string;
  notifyEmails: string;
}

const emptyForm: FormData = {
  name: '',
  priority: 0,
  isActive: true,
  conditions: {},
  assignToTechId: '',
  assignToTeamId: '',
  setProductLineId: '',
  notifyEmails: '',
};

export default function AutoAssignTab() {
  const { currentCompany } = useCompany();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { byKind: classificationsOf } = useClassifications();
  const priorityDefs = classificationsOf('priority');
  const sourceDefs = classificationsOf('source');

  const { data: rules = [] } = useQuery({
    queryKey: ['autoAssign'],
    queryFn: () => autoAssignApi.list(),
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['teams', companyId],
    queryFn: () => teamsApi.list(),
    enabled: !!editing,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => companiesApi.getMembers(companyId),
    enabled: !!editing,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', companyId],
    queryFn: () => categoriesApi.list(),
    enabled: !!editing,
  });

  const { data: productLines = [] } = useQuery({
    queryKey: ['product-lines', companyId],
    queryFn: () => productLinesApi.list(),
  });

  const technicians = (members as any[]).filter((m: any) => ['admin', 'technician', 'owner'].includes(m.role));

  const createMutation = useMutation({
    mutationFn: (data: any) => autoAssignApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoAssign'] });
      toast.success('Rule created');
      closeForm();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => autoAssignApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoAssign'] });
      toast.success('Rule updated');
      closeForm();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => autoAssignApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoAssign'] });
      toast.success('Rule deleted');
      setDeleteConfirm(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function closeForm() {
    setEditing(null);
    setForm(emptyForm);
  }

  function startEdit(rule: any) {
    setEditing(rule.id ?? rule._id);
    setForm({
      name: rule.name,
      priority: rule.priority,
      isActive: rule.isActive,
      conditions: rule.conditions || {},
      assignToTechId: rule.assignToTechId || '',
      assignToTeamId: rule.assignToTeamId || '',
      setProductLineId: rule.setProductLineId || '',
      notifyEmails: Array.isArray(rule.notifyEmails) ? rule.notifyEmails.join(', ') : '',
    });
  }

  function toggleConditionValue(condKey: 'categories' | 'priorities' | 'sources' | 'productLines', value: string) {
    setForm(prev => {
      const current = prev.conditions[condKey] || [];
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return { ...prev, conditions: { ...prev.conditions, [condKey]: next.length ? next : undefined } };
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.assignToTechId && !form.assignToTeamId && !form.setProductLineId) {
      return toast.error('Select a technician, team, or product line');
    }

    const payload: any = {
      name: form.name,
      priority: form.priority,
      isActive: form.isActive,
      conditions: form.conditions,
      assignToTechId: form.assignToTechId || null,
      assignToTeamId: form.assignToTeamId || null,
      setProductLineId: form.setProductLineId || null,
      notifyEmails: form.notifyEmails
        .split(/[,\n]/)
        .map(s => s.trim())
        .filter(Boolean),
    };

    if (editing === 'new') {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate({ id: editing!, data: payload });
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  function getAssigneeName(rule: any): string {
    const names: string[] = [];
    if (rule.assignToTechId) {
      const tech = (members as any[]).find((m: any) => (m.user?.id ?? m.userId) === rule.assignToTechId);
      names.push(tech?.user?.name || 'Technician');
    }
    if (rule.assignToTeamId) {
      const team = (teams as any[]).find((t: any) => (t.id ?? t._id) === rule.assignToTeamId);
      names.push(team?.name || 'Team');
    }
    return names.length ? names.join(' + ') : 'Unassigned';
  }

  function describeConditions(conditions: any): string {
    const parts: string[] = [];
    if (conditions?.priorities?.length) parts.push(`Priority: ${conditions.priorities.join(', ')}`);
    if (conditions?.categories?.length) parts.push(`Categories: ${conditions.categories.length}`);
    if (conditions?.sources?.length) parts.push(`Source: ${conditions.sources.join(', ')}`);
    if (conditions?.productLines?.length) parts.push(`Product lines: ${conditions.productLines.length}`);
    return parts.length ? parts.join(' | ') : 'All tickets';
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-title">Auto-Assign Rules</h2>
        {!editing && (
          <button
            className="btn btn-primary inline-flex items-center gap-2"
            onClick={() => { setEditing('new'); setForm({ ...emptyForm, priority: (rules as any[]).length }); }}
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 border border-line rounded-lg space-y-4 bg-row-odd">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-fg">
              {editing === 'new' ? 'New Auto-Assign Rule' : 'Edit Rule'}
            </h3>
            <button type="button" onClick={closeForm} className="text-muted hover:text-fg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Rule Name</label>
              <input
                className="input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Critical to Senior Tech"
              />
            </div>
            <div>
              <label className="label">Priority (higher = checked first)</label>
              <input
                type="number"
                className="input"
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm pb-2 text-fg">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  className="rounded accent-primary"
                />
                Active
              </label>
            </div>
          </div>

          <div>
            <label className="label mb-2">Conditions (match when ANY selected condition is met)</label>
            <div className="space-y-3">
              <div>
                <span className="text-xs font-medium text-muted uppercase tracking-wide">Ticket Priority</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {priorityDefs.map(p => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => toggleConditionValue('priorities', p.key)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        (form.conditions.priorities || []).includes(p.key)
                          ? 'bg-primary-muted border-primary text-primary'
                          : 'border-line text-muted hover:border-line'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {(categories as any[]).length > 0 && (
                <div>
                  <span className="text-xs font-medium text-muted uppercase tracking-wide">Category</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(categories as any[]).map((c: any) => (
                      <button
                        key={c.id ?? c._id}
                        type="button"
                        onClick={() => toggleConditionValue('categories', c.id ?? c._id)}
                        className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                          (form.conditions.categories || []).includes(c.id ?? c._id)
                            ? 'bg-primary-muted border-primary text-primary'
                            : 'border-line text-muted hover:border-line'
                        }`}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(productLines as any[]).length > 0 && (
                <div>
                  <span className="text-xs font-medium text-muted uppercase tracking-wide">Product Line</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(productLines as any[]).map((pl: any) => (
                      <button
                        key={pl.id}
                        type="button"
                        onClick={() => toggleConditionValue('productLines', pl.id)}
                        className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                          (form.conditions.productLines || []).includes(pl.id)
                            ? 'bg-primary-muted border-primary text-primary'
                            : 'border-line text-muted hover:border-line'
                        }`}
                      >
                        {pl.name}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted mt-1">
                    Tip: for product-line routing (e.g. email tickets), set ONLY a product-line
                    condition — conditions are OR'd, so adding Source: email too would catch every
                    email ticket regardless of product line.
                  </p>
                </div>
              )}

              <div>
                <span className="text-xs font-medium text-muted uppercase tracking-wide">Source</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {sourceDefs.map(src => (
                    <button
                      key={src.key}
                      type="button"
                      onClick={() => toggleConditionValue('sources', src.key)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        (form.conditions.sources || []).includes(src.key)
                          ? 'bg-primary-muted border-primary text-primary'
                          : 'border-line text-muted hover:border-line'
                      }`}
                    >
                      {src.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="label mb-2">Assign To</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-muted">Technician</label>
                <select
                  className="select w-full"
                  value={form.assignToTechId}
                  onChange={e => setForm(f => ({ ...f, assignToTechId: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {technicians.map((m: any) => (
                    <option key={m.user?.id ?? m.userId} value={m.user?.id ?? m.userId}>
                      {m.user?.name ?? m.user?.email} ({m.role})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted">Team</label>
                <select
                  className="select w-full"
                  value={form.assignToTeamId}
                  onChange={e => setForm(f => ({ ...f, assignToTeamId: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {(teams as any[]).map((t: any) => (
                    <option key={t.id ?? t._id} value={t.id ?? t._id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted">Set Product Line</label>
                <select
                  className="select w-full"
                  value={form.setProductLineId}
                  onChange={e => setForm(f => ({ ...f, setProductLineId: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {(productLines as any[]).map((pl: any) => (
                    <option key={pl.id} value={pl.id}>{pl.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-muted mt-1">
              You can set both a technician and a team — the technician gets the ticket and everyone
              on the team is notified when it's created. Set Product Line stamps that product line on
              matching tickets that don't already have one.
            </p>
          </div>

          <div>
            <label className="label">Notify these emails when this rule matches</label>
            <input
              className="input"
              value={form.notifyEmails}
              onChange={e => setForm(f => ({ ...f, notifyEmails: e.target.value }))}
              placeholder="e.g. hoa-board@example.com, gates@example.com"
            />
            <p className="text-xs text-muted mt-1">
              Comma-separated. These addresses get a "new ticket" alert when a ticket hits this rule —
              e.g. route gate-pass requests to the HOA, developer bugs to your dev team.
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : editing === 'new' ? 'Create Rule' : 'Update Rule'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancel</button>
          </div>
        </form>
      )}

      {(rules as any[]).length === 0 && !editing ? (
        <EmptyState icon={<Zap className="w-6 h-6" />}>
          No auto-assign rules yet. Click <strong>Add Rule</strong> to automatically route tickets to the right person or team.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {(rules as any[]).map((r: any) => (
            <div key={r.id ?? r._id} className={`flex items-start justify-between py-3 border-b border-line-light last:border-0 ${!r.isActive ? 'opacity-50' : ''}`}>
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <GripVertical className="w-4 h-4 text-muted mt-0.5 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-fg">{r.name}</span>
                    <span className="text-xs text-muted">P{r.priority}</span>
                    {!r.isActive && <span className="badge text-muted text-xs">Disabled</span>}
                  </div>
                  <p className="text-xs text-muted mt-0.5">{describeConditions(r.conditions)}</p>
                  <p className="text-xs text-muted">Assign to: <span className="font-medium text-fg">{getAssigneeName(r)}</span></p>
                  {r.setProductLineId && (
                    <p className="text-xs text-muted">
                      Sets product line:{' '}
                      <span className="font-medium text-fg">
                        {(productLines as any[]).find((pl: any) => pl.id === r.setProductLineId)?.name || 'Product line'}
                      </span>
                    </p>
                  )}
                  {Array.isArray(r.notifyEmails) && r.notifyEmails.length > 0 && (
                    <p className="text-xs text-muted">Notifies: <span className="font-medium text-fg">{r.notifyEmails.join(', ')}</span></p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-4 shrink-0">
                <button
                  className="p-1.5 text-muted hover:text-primary rounded transition-colors"
                  onClick={() => startEdit(r)}
                  title="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {deleteConfirm === (r.id ?? r._id) ? (
                  <div className="flex items-center gap-1">
                    <button
                      className="text-xs btn btn-danger py-1 px-2"
                      onClick={() => deleteMutation.mutate(r.id ?? r._id)}
                      disabled={deleteMutation.isPending}
                    >
                      Confirm
                    </button>
                    <button className="text-xs btn btn-ghost py-1 px-2" onClick={() => setDeleteConfirm(null)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    className="p-1.5 text-muted hover:text-err rounded transition-colors"
                    onClick={() => setDeleteConfirm(r.id ?? r._id)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
